import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { LandmarkPhoto } from '../types/landmark';

interface PhotoCarouselProps {
  photos: LandmarkPhoto[];
}

const SOURCE_LABEL: Record<LandmarkPhoto['source'], string> = {
  wikimedia: 'Wikimedia Commons',
  'onroerend-erfgoed-beeldbank': 'Onroerend Erfgoed Beeldbank',
  europeana: 'Europeana',
  rijksmuseum: 'Rijksmuseum',
  mapillary: 'Mapillary',
};

/**
 * Photo carousel for featured-tier landmarks.
 *
 * - Lazy-loaded images (loading="lazy")
 * - Keyboard nav via ArrowLeft/ArrowRight when the carousel has focus
 * - Click-anywhere dot indicators
 * - Per-photo attribution under the image (artist + license + source link)
 * - Wraps at edges so navigation always advances
 */
export function PhotoCarousel({ photos }: PhotoCarouselProps) {
  const [index, setIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const count = photos.length;

  const go = useCallback(
    (delta: number) => {
      if (count === 0) return;
      setIndex((prev) => (prev + delta + count) % count);
    },
    [count],
  );

  const handleKey = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        go(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        go(1);
      }
    },
    [go],
  );

  // Reset to first slide when the photo list changes (e.g., user picks a different landmark).
  useEffect(() => {
    setIndex(0);
  }, [photos]);

  if (count === 0) return null;
  const safeIndex = Math.min(index, count - 1);
  const current = photos[safeIndex];
  if (!current) return null;

  return (
    <div
      ref={containerRef}
      role="region"
      aria-label={`Photo ${safeIndex + 1} of ${count}`}
      aria-roledescription="carousel"
      tabIndex={0}
      onKeyDown={handleKey}
      data-testid="photo-carousel"
      style={{
        position: 'relative',
        width: '100%',
        background: 'var(--surface-hover)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        marginBottom: 'var(--space-3)',
      }}
    >
      <img
        src={current.thumbnailUrl ?? current.url}
        alt=""
        loading="lazy"
        data-testid="photo-carousel-image"
        style={{
          display: 'block',
          width: '100%',
          aspectRatio: '4 / 3',
          objectFit: 'cover',
        }}
      />

      {count > 1 ? (
        <>
          <CarouselButton direction="prev" onClick={() => go(-1)} />
          <CarouselButton direction="next" onClick={() => go(1)} />
          <div
            role="tablist"
            aria-label="Photo navigation"
            style={{
              position: 'absolute',
              bottom: 'var(--space-2)',
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              gap: 'var(--space-1)',
              pointerEvents: 'none',
            }}
          >
            {photos.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === safeIndex}
                aria-label={`Show photo ${i + 1}`}
                data-testid={`photo-carousel-dot-${i}`}
                onClick={() => setIndex(i)}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: i === safeIndex ? '#fffdf2' : 'rgba(255, 253, 242, 0.5)',
                  border: '1px solid rgba(28, 20, 16, 0.4)',
                  pointerEvents: 'auto',
                  padding: 0,
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>
        </>
      ) : null}

      <PhotoAttribution photo={current} />
    </div>
  );
}

function CarouselButton({ direction, onClick }: { direction: 'prev' | 'next'; onClick: () => void }) {
  const isPrev = direction === 'prev';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={isPrev ? 'Previous photo' : 'Next photo'}
      data-testid={`photo-carousel-${direction}`}
      style={{
        position: 'absolute',
        top: '50%',
        [isPrev ? 'left' : 'right']: 'var(--space-2)',
        transform: 'translateY(-50%)',
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: 'rgba(255, 253, 242, 0.85)',
        color: 'var(--text-headline)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      <svg
        width={16}
        height={16}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        {isPrev ? <path d="M15 18L9 12L15 6" /> : <path d="M9 6L15 12L9 18" />}
      </svg>
    </button>
  );
}

function PhotoAttribution({ photo }: { photo: LandmarkPhoto }) {
  const sourceLabel = SOURCE_LABEL[photo.source];
  return (
    <p
      data-testid="photo-attribution"
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(to top, rgba(28, 20, 16, 0.7), transparent)',
        color: '#fffdf2',
        padding: '20px var(--space-2) var(--space-2)',
        fontSize: '0.7rem',
        lineHeight: 1.3,
      }}
    >
      {photo.artist ? `${photo.artist} · ` : ''}
      <a
        href={photo.sourceRecordUrl ?? '#'}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'inherit', textDecoration: 'underline' }}
      >
        {sourceLabel}
      </a>{' '}
      ·{' '}
      {photo.licenseUrl ? (
        <a
          href={photo.licenseUrl}
          target="_blank"
          rel="noopener noreferrer license"
          style={{ color: 'inherit', textDecoration: 'underline' }}
        >
          {photo.license}
        </a>
      ) : (
        photo.license
      )}
    </p>
  );
}
